import PptxGenJS from "pptxgenjs";

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────

const TONE_ACCENT = {
  radieux:   "22d3ee",
  lucide:    "f59e0b",
  tragique:  "f87171",
  cynique:   "a78bfa",
  désespéré: "94a3b8",
  esperant:  "34d399",
};

const LEVEL_COLOR = {
  débutant:      "94a3b8",
  intermédiaire: "f59e0b",
  avancé:        "34d399",
  expert:        "22d3ee",
};

const pct = (v) => Math.max(0, Math.min(100, v ?? 0));

const getAction = (caseData, id) =>
  caseData?.steps?.flatMap((s) => s.actions || []).find((a) => a.id === id);

const corpus = (caseData, interp) => {
  const seen = new Set();
  return [...(caseData?.sources || []), ...(interp?.debriefSources || [])].filter((x) => {
    const k = `${x.title}|${x.author}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// PDF EXPORT — nouvelle fenêtre (bypass position:fixed)
// ─────────────────────────────────────────────────────────────────────────────

export function exportPdf(engine) {
  if (!engine) return;

  const { state, case: caseData } = engine;
  const sol    = state?.currentSolution || {};
  const interp = sol.interpretation    || {};
  const params = state?.parameters     || {};
  const initP  = state?.initialParameters || {};
  const done   = Array.from(state?.completedActions || []);
  const accent = TONE_ACCENT[interp.tone] || "f59e0b";
  const reportID = `PESACORE Architect-${Math.random().toString(36).substr(2, 6).toUpperCase()}`;
  const date = new Date().toLocaleDateString("fr-FR", {
    day: "2-digit", month: "long", year: "numeric",
  });

  // ── Construire le HTML complet
  const metricsRows = Object.entries(params).map(([k, v]) => {
    const init  = initP[k] ?? 50;
    const delta = v - init;
    const bar   = pct(v);
    const col   = bar >= 70 ? "#059669" : bar >= 40 ? "#b45309" : "#dc2626";
    const dyn   = caseData?.simulationEngine?.dynamics?.find((d) => d.parameter === k);
    return `
      <tr>
        <td style="padding:5px 8px;font-size:8pt;color:#555">${dyn?.reportLabel || k}</td>
        <td style="padding:5px 8px;font-size:11pt;font-weight:900;text-align:right;color:#111">${v}</td>
        <td style="padding:5px 8px;font-size:8pt;font-weight:700;text-align:right;color:${delta >= 0 ? "#059669" : "#dc2626"}">${delta >= 0 ? "+" : ""}${delta}</td>
        <td style="padding:5px 16px;width:120px">
          <div style="background:#e5e7eb;height:5px;border-radius:3px">
            <div style="background:${col};width:${bar}%;height:5px;border-radius:3px"></div>
          </div>
        </td>
      </tr>`;
  }).join("");

  const actionRows = done.map((id, i) => {
    const action  = getAction(caseData, id);
    const isPivot = id === interp.decisionAnalysis?.pivotActionId;
    return `
      <tr style="background:${isPivot ? "#fffbeb" : "transparent"}">
        <td style="padding:4px 8px;font-size:7.5pt;color:#aaa;width:28px;text-align:right">${i + 1}.</td>
        <td style="padding:4px 8px;font-size:9pt;font-weight:${isPivot ? 700 : 400}">${action?.label || id}</td>
        <td style="padding:4px 8px;font-size:7pt;color:#b45309;text-align:right">${isPivot ? "PIVOT" : ""}</td>
      </tr>`;
  }).join("");

  const frameworkRows = (interp.theoreticalFrameworks || [])
    .sort((a, b) => (b.relevanceScore || 0) - (a.relevanceScore || 0))
    .slice(0, 5)
    .map((fw) => `
      <div style="padding:8px 12px;margin-bottom:6px;background:${fw.type === "primary" ? "#fffbeb" : "#fafafa"};border:1px solid ${fw.type === "primary" ? "#fcd34d" : "#e5e7eb"}">
        <div style="font-weight:900;font-size:9.5pt">${fw.author} <span style="font-weight:400;font-style:italic;color:#555">— ${fw.concept}</span>
          <span style="font-size:7pt;color:#999;margin-left:8px">${Math.round((fw.relevanceScore || 0) * 100)}%</span>
        </div>
        <div style="font-size:8.5pt;color:#444;margin-top:2px;line-height:1.45">${fw.manifestedAs || ""}</div>
        ${fw.quote ? `<div style="font-size:7.5pt;color:#888;font-style:italic;margin-top:3px;padding-left:8px;border-left:2px solid #e5e7eb">"${fw.quote}"</div>` : ""}
      </div>`).join("");

  const compRows = (interp.competencies || []).map((c) => {
    const lv = { débutant: "#94a3b8", intermédiaire: "#b45309", avancé: "#059669", expert: "#0891b2" }[c.level] || "#888";
    return `
      <div style="padding:7px 10px;border:1px solid ${lv}30;background:${lv}08;margin-bottom:5px">
        <div style="display:flex;justify-content:space-between">
          <span style="font-weight:900;font-size:9.5pt;color:${lv}">${c.name}</span>
          <span style="font-size:7pt;text-transform:uppercase;color:${lv};border:1px solid ${lv}40;padding:1px 5px">${c.level}</span>
        </div>
        ${c.evidence ? `<div style="font-size:8pt;color:#555;font-style:italic;margin-top:3px">${c.evidence}</div>` : ""}
      </div>`;
  }).join("");

  const recRows = (interp.recommendations || [])
    .sort((a, b) => a.priority - b.priority)
    .map((r) => `
      <tr>
        <td style="padding:5px 8px;font-size:14pt;font-weight:900;color:${["", "#dc2626", "#b45309", "#059669", "#94a3b8"][r.priority] || "#888"};width:32px;text-align:right">${r.priority}</td>
        <td style="padding:5px 8px">
          <div style="font-size:9.5pt;font-weight:700">${r.action}</div>
          ${r.rationale ? `<div style="font-size:7.5pt;color:#666;font-style:italic">${r.rationale}</div>` : ""}
        </td>
        <td style="padding:5px 8px;font-size:7pt;color:#999;text-align:right;white-space:nowrap">${r.timeframe || ""}${r.actor ? `<br>${r.actor}` : ""}</td>
      </tr>`).join("");

  const sourceRows = corpus(caseData, interp).map((src, i) => `
    <tr>
      <td style="padding:3px 6px;font-size:7pt;color:#aaa;width:24px;text-align:right">[${i + 1}]</td>
      <td style="padding:3px 6px;font-size:8pt;font-weight:700">${src.title}</td>
      <td style="padding:3px 6px;font-size:7.5pt;color:#666;font-style:italic">${src.author}${src.year ? `, ${src.year}` : ""}</td>
      ${src.url ? `<td style="padding:3px 6px;font-size:7pt"><a href="${src.url}" style="color:#2563eb">${src.url.replace("https://", "").slice(0, 32)}…</a></td>` : "<td></td>"}
    </tr>`).join("");

  const gap = Math.abs((interp.theoreticalPromise ?? 0) - (interp.realWorldProbability ?? 0));

  const html = `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <title>PESACORE — ${sol.name || "Rapport"}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: "Courier New", monospace;
      font-size: 9pt;
      line-height: 1.5;
      color: #111;
      background: #fff;
    }
    @page { size: A4 portrait; margin: 16mm 14mm; }
    @media print {
      .no-print { display: none !important; }
      table { page-break-inside: avoid; }
      .section { page-break-inside: avoid; margin-bottom: 18pt; }
    }
    .label { font-size: 6.5pt; text-transform: uppercase; letter-spacing: 2px; color: #999; margin-bottom: 3px; }
    .section { margin-bottom: 20pt; }
    .section-title { font-size: 6.5pt; text-transform: uppercase; letter-spacing: 2.5px; color: #888; border-bottom: 1px solid #e5e7eb; padding-bottom: 3px; margin-bottom: 10px; }
    table { width: 100%; border-collapse: collapse; }
    .kpi-box { border: 1px solid #e5e7eb; padding: 8px 12px; background: #fafafa; }
  </style>
</head>
<body>

<!-- BOUTON IMPRIMER (masqué à l'impression) -->
<div class="no-print" style="position:sticky;top:0;background:#fff;z-index:10;padding:10px 16px;border-bottom:1px solid #e5e7eb;display:flex;gap:12px;align-items:center">
  <button onclick="window.print()" style="background:#111;color:#fff;border:none;padding:8px 20px;font-size:9pt;font-weight:700;cursor:pointer;letter-spacing:1px;text-transform:uppercase">
    ↓ Imprimer / Enregistrer PDF
  </button>
  <span style="font-size:7.5pt;color:#999">Ctrl+P → Enregistrer en PDF · Recommandé : marges 15mm, pas d'en-tête/pied de page navigateur</span>
</div>

<!-- EN-TÊTE -->
<div style="margin-bottom:22pt;padding-bottom:12pt;border-bottom:2px solid #111">
  <div class="label">${reportID} · PESACORE Architect Engine v1.0 · ${date}</div>
  <div style="font-size:24pt;font-weight:900;text-transform:uppercase;letter-spacing:-1px;line-height:1;margin:5px 0">${sol.name || "ANALYSE TERMINÉE"}</div>
  ${interp.shortSummary ? `<div style="font-size:10.5pt;font-style:italic;color:#555;margin-top:5px;line-height:1.4">"${interp.shortSummary}"</div>` : ""}
</div>

<!-- PROBABILITÉS -->
<div class="section">
  <table style="border:1px solid #e5e7eb">
    <tr>
      <td style="padding:10px 14px;border-right:1px solid #e5e7eb">
        <div class="label">Optimum théorique</div>
        <div style="font-size:26pt;font-weight:900">${interp.theoreticalPromise ?? "--"}%</div>
        <div style="font-size:7pt;color:#aaa">Optimisation génétique</div>
      </td>
      <td style="padding:10px 14px;text-align:center;border-right:1px solid #e5e7eb">
        <div class="label">Écart de reproductibilité</div>
        <div style="font-size:26pt;font-weight:900;color:${gap < 30 ? "#059669" : "#dc2626"}">−${gap.toFixed(0)}%</div>
        <div style="font-size:7pt;color:#aaa">théorique → réel</div>
      </td>
      <td style="padding:10px 14px;text-align:right">
        <div class="label">Probabilité réelle (1900–2025)</div>
        <div style="font-size:26pt;font-weight:900;color:#${accent}">${interp.realWorldProbability ?? "--"}%</div>
        <div style="font-size:7pt;color:#aaa">Corpus historique calibré</div>
      </td>
    </tr>
  </table>
</div>

<!-- MESSAGES CLÉS -->
${interp.keyInsight || interp.pedagogicalMessage || interp.counterArgument ? `
<div class="section">
  <div class="section-title">Messages clés</div>
  <table><tr>
    ${interp.keyInsight ? `<td style="padding:8px 10px;background:#fffbeb;border:1px solid #fcd34d;width:50%;vertical-align:top">
      <div class="label" style="color:#b45309">Insight moteur</div>
      <div style="font-size:9pt;font-style:italic;color:#1c1917">"${interp.keyInsight}"</div>
    </td>` : ""}
    ${interp.pedagogicalMessage ? `<td style="padding:8px 10px;background:#f0f9ff;border:1px solid #bae6fd;width:50%;vertical-align:top">
      <div class="label" style="color:#0369a1">Lecture pédagogique</div>
      <div style="font-size:8.5pt;color:#1e3a8a;line-height:1.5">${interp.pedagogicalMessage}</div>
    </td>` : ""}
  </tr></table>
  ${interp.counterArgument ? `<div style="margin-top:6px;padding:8px 10px;background:#fef2f2;border:1px solid #fecaca">
    <div class="label" style="color:#dc2626">Angle mort / Risque critique</div>
    <div style="font-size:9pt;color:#7f1d1d;line-height:1.5">${interp.counterArgument}</div>
  </div>` : ""}
</div>` : ""}

<!-- MÉTRIQUES SYSTÈME -->
${Object.keys(params).length > 0 ? `
<div class="section">
  <div class="section-title">Métriques finales du système (${Object.keys(params).length} paramètres)</div>
  <table>
    <thead>
      <tr style="background:#f9fafb">
        <th style="padding:5px 8px;text-align:left;font-size:7pt;color:#888;font-weight:700;text-transform:uppercase">Paramètre</th>
        <th style="padding:5px 8px;text-align:right;font-size:7pt;color:#888;font-weight:700;text-transform:uppercase">Finale</th>
        <th style="padding:5px 8px;text-align:right;font-size:7pt;color:#888;font-weight:700;text-transform:uppercase">Delta</th>
        <th style="padding:5px 16px;font-size:7pt;color:#888;font-weight:700;text-transform:uppercase">Progression</th>
      </tr>
    </thead>
    <tbody>${metricsRows}</tbody>
  </table>
</div>` : ""}

<!-- TRAJECTOIRE -->
${done.length > 0 ? `
<div class="section">
  <div class="section-title">Séquence décisionnelle (${done.length} actions)</div>
  ${interp.decisionAnalysis?.narrative ? `<div style="padding:8px 10px;background:#f9fafb;border:1px solid #e5e7eb;font-style:italic;font-size:9pt;margin-bottom:8px;line-height:1.5">${interp.decisionAnalysis.narrative}</div>` : ""}
  <table>${actionRows}</table>
  ${interp.decisionAnalysis?.missedOpportunities?.length > 0 ? `
  <div style="margin-top:8px">
    <div class="label">Chemins non pris</div>
    ${interp.decisionAnalysis.missedOpportunities.map((o, i) => `<div style="font-size:8pt;color:#666;font-style:italic;padding:3px 0 3px 14px;border-left:2px solid #e5e7eb;margin-bottom:3px">${o}</div>`).join("")}
  </div>` : ""}
</div>` : ""}

<!-- THÉORIE -->
${(interp.theoreticalFrameworks || []).length > 0 ? `
<div class="section">
  <div class="section-title">Ancrage théorique</div>
  ${frameworkRows}
</div>` : ""}

<!-- COMPÉTENCES -->
${(interp.competencies || []).length > 0 ? `
<div class="section">
  <div class="section-title">Compétences démontrées</div>
  <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px">${compRows}</div>
</div>` : ""}

<!-- TRANSFERT -->
${(interp.transferability || interp.recommendations?.length > 0) ? `
<div class="section">
  <div class="section-title">Transfert & Plan d'action</div>
  ${(interp.transferability?.toSchool || interp.transferability?.toEnterprise || interp.transferability?.toPolicy) ? `
  <table style="margin-bottom:10px"><tr>
    ${interp.transferability?.toSchool ? `<td style="padding:7px 9px;background:#eff6ff;border:1px solid #bfdbfe;vertical-align:top;width:33%"><div class="label" style="color:#1d4ed8">École / Formation</div><div style="font-size:8.5pt;color:#1e3a8a">${interp.transferability.toSchool}</div></td>` : ""}
    ${interp.transferability?.toEnterprise ? `<td style="padding:7px 9px;background:#faf5ff;border:1px solid #e9d5ff;vertical-align:top;width:33%"><div class="label" style="color:#7c3aed">Organisation</div><div style="font-size:8.5pt;color:#4c1d95">${interp.transferability.toEnterprise}</div></td>` : ""}
    ${interp.transferability?.toPolicy ? `<td style="padding:7px 9px;background:#f0fdf4;border:1px solid #bbf7d0;vertical-align:top;width:33%"><div class="label" style="color:#15803d">Politique publique</div><div style="font-size:8.5pt;color:#14532d">${interp.transferability.toPolicy}</div></td>` : ""}
  </tr></table>` : ""}
  ${(interp.recommendations || []).length > 0 ? `<table><tbody>${recRows}</tbody></table>` : ""}
</div>` : ""}

<!-- DÉBAT -->
${(interp.openQuestions || []).length > 0 ? `
<div class="section">
  <div class="section-title">Questions ouvertes</div>
  ${(interp.openQuestions || []).map(q => `<div style="padding:5px 0 5px 14px;border-left:2px solid #fcd34d;margin-bottom:5px;font-size:9pt;color:#444;font-style:italic">${q}</div>`).join("")}
</div>` : ""}

<!-- CORPUS -->
${corpus(caseData, interp).length > 0 ? `
<div class="section">
  <div class="section-title">Corpus de référence</div>
  <table><tbody>${sourceRows}</tbody></table>
</div>` : ""}

<!-- PIED DE PAGE -->
<div style="margin-top:28pt;padding-top:10pt;border-top:1px solid #e5e7eb;display:flex;justify-content:space-between;font-size:6.5pt;color:#aaa;text-transform:uppercase;letter-spacing:1.5px">
  <span>PESACORE Architect Engine v1.0 — Rapport d'Enquête Systémique</span>
  <span>${reportID} · CONFIDENTIEL</span>
</div>

</body>
</html>`;

  const win = window.open("", "_blank", "width=900,height=1100");
  if (!win) {
    alert("Popup bloqué. Autorisez les popups pour ce site pour exporter en PDF.");
    return;
  }
  win.document.write(html);
  win.document.close();
  // Laisser le temps au rendu avant d'ouvrir l'impression automatiquement
  setTimeout(() => win.print(), 600);
}

// ─────────────────────────────────────────────────────────────────────────────
// PPTX EXPORT — pptxgenjs, style sombre premium
// ─────────────────────────────────────────────────────────────────────────────

export async function exportPptx(engine) {
  if (!engine) return;

  const { state, case: caseData } = engine;
  const sol    = state?.currentSolution || {};
  const interp = sol.interpretation    || {};
  const params = state?.parameters     || {};
  const initP  = state?.initialParameters || {};
  const done   = Array.from(state?.completedActions || []);
  const accent = TONE_ACCENT[interp.tone] || "f59e0b";
  const date   = new Date().toLocaleDateString("fr-FR");

  const pres = new PptxGenJS();
  pres.layout  = "LAYOUT_16x9";
  pres.author  = "PESACORE Architect Engine v1.0";
  pres.title   = sol.name || "Rapport d'Enquête";

  // Palette : fond quasi-noir, accent dynamique, texte blanc/gris
  const BG     = "080808";
  const BG2    = "111111";
  const ACCENT = accent;
  const WHITE  = "FFFFFF";
  const GREY   = "94A3B8";
  const GREY2  = "374151";

  // Helper : fond de slide
  const baseBg = (slide) => {
    slide.background = { color: BG };
  };

  // Helper : bande titre gauche verticale
  const accentBar = (slide, h = 5.625) => {
    slide.addShape(pres.shapes.RECTANGLE, {
      x: 0, y: 0, w: 0.06, h,
      fill: { color: ACCENT }, line: { color: ACCENT },
    });
  };

  // Helper : texte caption
  const caption = (slide, text, x, y, w = 4) => {
    slide.addText(text, {
      x, y, w, h: 0.2,
      fontSize: 6, color: GREY, bold: true,
      charSpacing: 3, align: "left",
    });
  };

  // Helper : card (rectangle blanc semi-transparent)
  const card = (slide, x, y, w, h, col = "1A1A1A") => {
    slide.addShape(pres.shapes.RECTANGLE, {
      x, y, w, h,
      fill: { color: col }, line: { color: "FFFFFF", transparency: 90, width: 0.5 },
    });
  };

  // ── SLIDE 1 : COUVERTURE ─────────────────────────────────────────────────

  const s1 = pres.addSlide();
  baseBg(s1);

  // Grand rectangle accent en fond
  s1.addShape(pres.shapes.RECTANGLE, {
    x: 0, y: 0, w: 10, h: 5.625,
    fill: { color: ACCENT, transparency: 92 },
    line: { color: ACCENT },
  });
  accentBar(s1);

  // Badge PESA
  s1.addShape(pres.shapes.RECTANGLE, {
    x: 0.35, y: 0.3, w: 0.9, h: 0.22,
    fill: { color: ACCENT, transparency: 80 }, line: { color: ACCENT, width: 0.5 },
  });
  s1.addText("PESACORE Architect · v1.0", {
    x: 0.35, y: 0.3, w: 0.9, h: 0.22,
    fontSize: 5.5, color: ACCENT, bold: true, align: "center", charSpacing: 2, margin: 0,
  });

  // Nom de la solution
  s1.addText(sol.name || "ANALYSE TERMINÉE", {
    x: 0.35, y: 1.1, w: 6, h: 2,
    fontSize: 36, color: WHITE, bold: true,
    align: "left", valign: "top",
    charSpacing: -1,
  });

  // Summary
  if (interp.shortSummary) {
    s1.addText(`"${interp.shortSummary}"`, {
      x: 0.35, y: 3.2, w: 6.5, h: 0.8,
      fontSize: 11, color: `${ACCENT}`, italic: true, align: "left",
    });
  }

  // Probas — 3 blocs droits
  const probas = [
    { l: "THÉORIQUE", v: `${interp.theoreticalPromise ?? "--"}%`, c: WHITE },
    { l: "ÉCART", v: `-${Math.abs((interp.theoreticalPromise ?? 0) - (interp.realWorldProbability ?? 0)).toFixed(0)}%`, c: "f87171" },
    { l: "RÉEL", v: `${interp.realWorldProbability ?? "--"}%`, c: ACCENT },
  ];
  probas.forEach((p, i) => {
    card(s1, 7.2 + i * 0.88, 1.2, 0.8, 1.1);
    s1.addText(p.v, {
      x: 7.2 + i * 0.88, y: 1.25, w: 0.8, h: 0.65,
      fontSize: 20, color: p.c, bold: true, align: "center", valign: "middle",
    });
    s1.addText(p.l, {
      x: 7.2 + i * 0.88, y: 1.9, w: 0.8, h: 0.2,
      fontSize: 4.5, color: GREY, bold: true, align: "center", charSpacing: 1,
    });
  });

  // Pied
  s1.addText(`${date} · CONFIDENTIEL`, {
    x: 0.35, y: 5.2, w: 9.3, h: 0.2,
    fontSize: 6, color: GREY, align: "left", charSpacing: 1,
  });

  // ── SLIDE 2 : SYNTHÈSE ───────────────────────────────────────────────────

  const s2 = pres.addSlide();
  baseBg(s2);
  accentBar(s2);
  caption(s2, "SYNTHÈSE", 0.25, 0.2);
  s2.addText("Messages clés", {
    x: 0.25, y: 0.45, w: 9.5, h: 0.5,
    fontSize: 22, color: WHITE, bold: true,
  });

  const msgs = [
    interp.keyInsight     && { l: "Insight moteur",      t: `"${interp.keyInsight}"`,      c: ACCENT },
    interp.pedagogicalMessage && { l: "Lecture pédagogique", t: interp.pedagogicalMessage, c: WHITE },
    interp.counterArgument && { l: "Angle mort",          t: interp.counterArgument,        c: "f87171" },
  ].filter(Boolean);

  msgs.forEach((m, i) => {
    const y = 1.15 + i * 1.35;
    card(s2, 0.25, y, 9.5, 1.2, "1A1A1A");
    s2.addShape(pres.shapes.RECTANGLE, { x: 0.25, y, w: 0.06, h: 1.2, fill: { color: m.c }, line: { color: m.c } });
    s2.addText(m.l.toUpperCase(), {
      x: 0.45, y: y + 0.08, w: 9, h: 0.18,
      fontSize: 5.5, color: m.c, bold: true, charSpacing: 2,
    });
    s2.addText(m.t, {
      x: 0.45, y: y + 0.3, w: 9, h: 0.8,
      fontSize: 9, color: m.c === WHITE ? "D1D5DB" : `${m.c}CC`,
      italic: m.c === ACCENT, valign: "top",
    });
  });

  // ── SLIDE 3 : MÉTRIQUES SYSTÈME ─────────────────────────────────────────

  const paramEntries = Object.entries(params).slice(0, 8);
  if (paramEntries.length > 0) {
    const s3 = pres.addSlide();
    baseBg(s3);
    accentBar(s3);
    caption(s3, "SYSTÈME", 0.25, 0.2);
    s3.addText("Métriques finales", {
      x: 0.25, y: 0.45, w: 9.5, h: 0.5, fontSize: 22, color: WHITE, bold: true,
    });

    const cols = 2;
    paramEntries.forEach(([k, v], i) => {
      const init  = initP[k] ?? 50;
      const delta = v - init;
      const bar   = pct(v);
      const col   = bar >= 70 ? "34d399" : bar >= 40 ? "f59e0b" : "f87171";
      const col2  = Math.floor(i / cols);
      const col3  = i % cols;
      const x = 0.25 + col3 * 4.85;
      const y = 1.15 + col2 * 1.0;
      const dyn = caseData?.simulationEngine?.dynamics?.find((d) => d.parameter === k);

      card(s3, x, y, 4.6, 0.85, "151515");
      s3.addText((dyn?.reportLabel || k).replace(/([A-Z])/g, " $1").trim().toUpperCase(), {
        x: x + 0.12, y: y + 0.07, w: 3.5, h: 0.15,
        fontSize: 5.5, color: GREY, bold: true, charSpacing: 2,
      });
      s3.addText(`${v}`, {
        x: x + 0.12, y: y + 0.26, w: 1.2, h: 0.4,
        fontSize: 22, color: WHITE, bold: true,
      });
      s3.addText(`${delta >= 0 ? "+" : ""}${delta}`, {
        x: x + 1.2, y: y + 0.36, w: 0.6, h: 0.25,
        fontSize: 10, color: delta >= 0 ? "34d399" : "f87171", bold: true,
      });
      // Barre progression
      s3.addShape(pres.shapes.RECTANGLE, {
        x: x + 0.12, y: y + 0.7, w: 3.8, h: 0.06,
        fill: { color: GREY2 }, line: { color: GREY2 },
      });
      s3.addShape(pres.shapes.RECTANGLE, {
        x: x + 0.12, y: y + 0.7, w: Math.max(0.06, 3.8 * bar / 100), h: 0.06,
        fill: { color: col }, line: { color: col },
      });
    });
  }

  // ── SLIDE 4 : TRAJECTOIRE ────────────────────────────────────────────────

  if (done.length > 0) {
    const s4 = pres.addSlide();
    baseBg(s4);
    accentBar(s4);
    caption(s4, "TRAJECTOIRE", 0.25, 0.2);
    s4.addText("Séquence décisionnelle", {
      x: 0.25, y: 0.45, w: 9.5, h: 0.5, fontSize: 22, color: WHITE, bold: true,
    });

    if (interp.decisionAnalysis?.narrative) {
      card(s4, 0.25, 1.05, 9.5, 0.6, "151505");
      s4.addText(interp.decisionAnalysis.narrative, {
        x: 0.4, y: 1.1, w: 9.2, h: 0.5,
        fontSize: 8.5, color: `${ACCENT}BB`, italic: true, valign: "middle",
      });
    }

    const visibleActions = done.slice(0, 7);
    visibleActions.forEach((id, i) => {
      const action  = getAction(caseData, id);
      const isPivot = id === interp.decisionAnalysis?.pivotActionId;
      const y = 1.8 + i * 0.53;

      if (isPivot) {
        s4.addShape(pres.shapes.RECTANGLE, {
          x: 0.25, y, w: 9.5, h: 0.46,
          fill: { color: ACCENT, transparency: 88 }, line: { color: ACCENT, width: 0.5 },
        });
      }

      s4.addText(`${String(i + 1).padStart(2, "0")}`, {
        x: 0.3, y: y + 0.12, w: 0.35, h: 0.22,
        fontSize: 8, color: GREY, bold: true, align: "right",
      });
      s4.addText(action?.label || id, {
        x: 0.75, y: y + 0.08, w: 7.8, h: 0.3,
        fontSize: isPivot ? 10 : 9, color: isPivot ? ACCENT : "D1D5DB",
        bold: isPivot, valign: "middle",
      });
      if (isPivot) {
        s4.addText("PIVOT", {
          x: 8.8, y: y + 0.12, w: 0.8, h: 0.22,
          fontSize: 6, color: ACCENT, bold: true, align: "center", charSpacing: 1,
        });
      }
    });

    if (done.length > 7) {
      s4.addText(`+ ${done.length - 7} actions supplémentaires`, {
        x: 0.3, y: 5.25, w: 9, h: 0.2,
        fontSize: 7, color: GREY, italic: true,
      });
    }
  }

  // ── SLIDE 5 : THÉORIE ────────────────────────────────────────────────────

  const tfs = (interp.theoreticalFrameworks || [])
    .sort((a, b) => (b.relevanceScore || 0) - (a.relevanceScore || 0))
    .slice(0, 4);

  if (tfs.length > 0) {
    const s5 = pres.addSlide();
    baseBg(s5);
    accentBar(s5);
    caption(s5, "THÉORIE", 0.25, 0.2);
    s5.addText("Ancrage théorique", {
      x: 0.25, y: 0.45, w: 9.5, h: 0.5, fontSize: 22, color: WHITE, bold: true,
    });

    tfs.forEach((fw, i) => {
      const isPrimary = fw.type === "primary";
      const col2 = Math.floor(i / 2);
      const col3 = i % 2;
      const x = 0.25 + col3 * 4.85;
      const y = 1.15 + col2 * 2.15;
      const bord = isPrimary ? ACCENT : GREY2;

      card(s5, x, y, 4.6, 2.0, isPrimary ? "1A1500" : "141414");
      s5.addShape(pres.shapes.RECTANGLE, { x, y, w: 0.06, h: 2.0, fill: { color: bord }, line: { color: bord } });

      s5.addText(fw.author, {
        x: x + 0.18, y: y + 0.1, w: 3.5, h: 0.3,
        fontSize: 11, color: isPrimary ? ACCENT : WHITE, bold: true,
      });
      // Barre relevance
      const rel = Math.round((fw.relevanceScore || 0) * 100);
      s5.addShape(pres.shapes.RECTANGLE, {
        x: x + 0.18, y: y + 0.42, w: 3.6, h: 0.07,
        fill: { color: GREY2 }, line: { color: GREY2 },
      });
      s5.addShape(pres.shapes.RECTANGLE, {
        x: x + 0.18, y: y + 0.42, w: Math.max(0.06, 3.6 * rel / 100), h: 0.07,
        fill: { color: isPrimary ? ACCENT : "60a5fa" }, line: { color: "transparent" },
      });
      s5.addText(`${rel}%`, {
        x: x + 3.85, y: y + 0.38, w: 0.6, h: 0.18,
        fontSize: 6.5, color: GREY, align: "right",
      });

      s5.addText(fw.concept || "", {
        x: x + 0.18, y: y + 0.56, w: 4.2, h: 0.18,
        fontSize: 7.5, color: GREY, italic: true,
      });
      if (fw.manifestedAs) {
        s5.addText(fw.manifestedAs, {
          x: x + 0.18, y: y + 0.78, w: 4.2, h: 0.75,
          fontSize: 8, color: "D1D5DB", valign: "top",
        });
      }
      if (fw.quote) {
        s5.addText(`"${fw.quote.slice(0, 90)}${fw.quote.length > 90 ? "…" : ""}"`, {
          x: x + 0.18, y: y + 1.6, w: 4.2, h: 0.28,
          fontSize: 7, color: GREY, italic: true, valign: "bottom",
        });
      }
    });
  }

  // ── SLIDE 6 : COMPÉTENCES ────────────────────────────────────────────────

  const comps = interp.competencies || [];
  if (comps.length > 0) {
    const s6 = pres.addSlide();
    baseBg(s6);
    accentBar(s6);
    caption(s6, "COMPÉTENCES", 0.25, 0.2);
    s6.addText("Compétences démontrées", {
      x: 0.25, y: 0.45, w: 9.5, h: 0.5, fontSize: 22, color: WHITE, bold: true,
    });

    comps.slice(0, 6).forEach((comp, i) => {
      const lv  = LEVEL_COLOR[comp.level] || "94a3b8";
      const col2 = Math.floor(i / 3);
      const row  = i % 3;
      const x = 0.25 + col2 * 4.85;
      const y = 1.15 + row * 1.45;

      card(s6, x, y, 4.6, 1.3, "141414");
      s6.addShape(pres.shapes.RECTANGLE, { x, y, w: 0.06, h: 1.3, fill: { color: lv }, line: { color: lv } });
      s6.addText(comp.name, {
        x: x + 0.2, y: y + 0.1, w: 3.5, h: 0.3,
        fontSize: 11, color: `${lv}`, bold: true,
      });
      s6.addText(comp.level?.toUpperCase() || "", {
        x: x + 3.5, y: y + 0.13, w: 0.9, h: 0.22,
        fontSize: 5.5, color: lv, bold: true, align: "right", charSpacing: 1,
      });
      if (comp.evidence) {
        s6.addText(comp.evidence, {
          x: x + 0.2, y: y + 0.48, w: 4.2, h: 0.72,
          fontSize: 7.5, color: "9CA3AF", valign: "top",
        });
      }
    });
  }

  // ── SLIDE 7 : PLAN D'ACTION ──────────────────────────────────────────────

  const recs = (interp.recommendations || []).sort((a, b) => a.priority - b.priority);
  if (recs.length > 0) {
    const s7 = pres.addSlide();
    baseBg(s7);
    accentBar(s7);
    caption(s7, "TRANSFERT", 0.25, 0.2);
    s7.addText("Plan d'action recommandé", {
      x: 0.25, y: 0.45, w: 9.5, h: 0.5, fontSize: 22, color: WHITE, bold: true,
    });

    const PCOL = ["", "f87171", "f59e0b", "34d399", "94a3b8"];
    recs.slice(0, 6).forEach((rec, i) => {
      const y  = 1.15 + i * 0.73;
      const pc = PCOL[rec.priority] || GREY;
      card(s7, 0.25, y, 9.5, 0.65, "141414");
      s7.addText(`${rec.priority}`, {
        x: 0.3, y: y + 0.1, w: 0.4, h: 0.45,
        fontSize: 20, color: pc, bold: true, align: "center", valign: "middle",
      });
      s7.addText(rec.action, {
        x: 0.82, y: y + 0.07, w: 7, h: 0.28,
        fontSize: 10, color: WHITE, bold: true,
      });
      if (rec.rationale) {
        s7.addText(rec.rationale, {
          x: 0.82, y: y + 0.36, w: 7, h: 0.24,
          fontSize: 7.5, color: "9CA3AF", italic: true,
        });
      }
      if (rec.timeframe) {
        s7.addText(rec.timeframe, {
          x: 8.1, y: y + 0.1, w: 1.5, h: 0.2,
          fontSize: 6.5, color: GREY, align: "right", bold: true, charSpacing: 1,
        });
      }
    });
  }

  // ── SLIDE 8 : DÉBAT & QUESTIONS ──────────────────────────────────────────

  const qs = interp.openQuestions || [];
  if (qs.length > 0) {
    const s8 = pres.addSlide();
    baseBg(s8);
    accentBar(s8);
    caption(s8, "DÉBAT", 0.25, 0.2);
    s8.addText("Questions pour le débat", {
      x: 0.25, y: 0.45, w: 9.5, h: 0.5, fontSize: 22, color: WHITE, bold: true,
    });

    qs.slice(0, 6).forEach((q, i) => {
      const col2 = Math.floor(i / 2);
      const col3 = i % 2;
      const x = 0.25 + col3 * 4.85;
      const y = 1.15 + col2 * 1.45;

      card(s8, x, y, 4.6, 1.3, "141414");
      s8.addText("?", {
        x: x + 0.12, y: y + 0.08, w: 0.45, h: 0.55,
        fontSize: 28, color: `${ACCENT}99`, bold: true, align: "center", valign: "middle",
      });
      s8.addText(q, {
        x: x + 0.65, y: y + 0.12, w: 3.8, h: 1.0,
        fontSize: 8.5, color: "D1D5DB", italic: true, valign: "top",
      });
    });
  }

  // ── SLIDE FIN ────────────────────────────────────────────────────────────

  const sf = pres.addSlide();
  sf.background = { color: BG };
  sf.addShape(pres.shapes.RECTANGLE, {
    x: 0, y: 0, w: 10, h: 5.625,
    fill: { color: ACCENT, transparency: 94 }, line: { color: ACCENT },
  });
  accentBar(sf);
  sf.addText("PESACORE Architect", {
    x: 0.35, y: 1.5, w: 9, h: 1.2,
    fontSize: 72, color: ACCENT, bold: true, align: "left", charSpacing: -3,
  });
  sf.addText("Fin du rapport", {
    x: 0.35, y: 2.9, w: 9, h: 0.5,
    fontSize: 16, color: WHITE, align: "left",
  });
  sf.addText(`${caseData?.slug?.toUpperCase() || ""} · ${date} · CONFIDENTIEL`, {
    x: 0.35, y: 5.2, w: 9.3, h: 0.2,
    fontSize: 6, color: GREY, align: "left", charSpacing: 1,
  });

  // ── TÉLÉCHARGEMENT ───────────────────────────────────────────────────────

  const slug = caseData?.slug || "rapport";
  await pres.writeFile({ fileName: `PESACORE Architect_${slug}_${date.replace(/\//g, "-")}.pptx` });
}